import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';

import { AppService } from '@app/app.service';
import { AuthUtilsService } from '@app/auth/services/auth-utils.service';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { EndUserData } from '@app/core/interfaces/end-user-data';
import { EnterpriseOptionType } from '@app/core/interfaces/enterprise-option-type';
import { UserType } from '@app/core/interfaces/user-type';
import { AuthUser } from '@app/core/models/auth-user';
import { SecurityPermissionName } from '@app/core/models/permission';
import { UserLevel, userLevelIsEnough } from '@app/core/models/user-level';
import { NotificationService } from '@app/core/services/notification.service';
import { RouterService } from '@app/routing/router.service';
import { License } from '@app/shared/models/license';
import { AuthorizationService } from '@app/shared/services/authorization.service';
import { EndUserContextService } from '@app/shared/services/context/end-user-context.service';
import { EnterpriseAuthorizationService } from '@app/shared/services/enterprise-authorization.service';
import { loggerFor } from '@app/utils/logger';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, merge, Observable, of } from 'rxjs';
import { filter, first, map, mergeMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AuthGuardService {
  private logger = loggerFor('AuthGuard');

  constructor(
    private appService: AppService,
    private authenticationService: AuthenticationService,
    private authorizationService: AuthorizationService,
    private enterpriseAuthorizationService: EnterpriseAuthorizationService,
    private authUtilsService: AuthUtilsService,
    private routerService: RouterService,
    private endUserContextService: EndUserContextService,
    private notificationService: NotificationService,
    private translateService: TranslateService
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.appService.initialized$.pipe(
      filter((initialized) => initialized === true),
      map(() => {
        let resumeNavigation: boolean;

        const authenticated = this.authenticationService.isAuthenticated;
        const user = this.authenticationService.authUser;

        if (!authenticated) {
          this.authUtilsService.redirectToLogin(state.url);
          resumeNavigation = false;
        } else {
          resumeNavigation = this.checkUserLevelAndPermissions(route, state, user);
        }

        return resumeNavigation;
      }),
      map((resumeNavigation) => {
        if (resumeNavigation) {
          this.checkUserAgainstRoute(route, state, this.authenticationService.authUser);
        }

        return resumeNavigation;
      }),
      mergeMap((resumeNavigation) => this.checkEnterpriseOptionsAndLicense(resumeNavigation, route)),
      mergeMap((resumeNavigation: boolean) => this.checkEndUserType(resumeNavigation, route)),
      mergeMap((resumeNavigation) => this.checkAuthorization$(resumeNavigation, route))
    );
  }

  /**
   * handling a case of some end-user route that should not be accessible, such as devices for end-user without devices,
   * check is based on "local user" `USER_TYPE` enum.
   */
  private checkEndUserType(resumeNavigation: boolean, route: ActivatedRouteSnapshot): Observable<boolean> {
    const allowedEndUserTypes: UserType[] = route.data.allowedEndUserTypes as UserType[] | null;

    if (resumeNavigation && allowedEndUserTypes != null) {
      this.logger.debug('Check if the context user has the allowed user type from', allowedEndUserTypes);
      return merge(
        this.endUserContextService.endUser.data$,
        this.endUserContextService.endUser.error$.pipe(filter((e) => e != null))
      ).pipe(
        first(),
        map((something) => {
          if ((something as any).cause?.cause?.error?.exception === 'repositories.c5.C5NotFoundException') {
            return true;
          } else {
            const endUser = something as EndUserData;
            let resumeNavigation = false;
            const userType = endUser.localData.user.userType;

            if (userType != null) {
              resumeNavigation = allowedEndUserTypes.includes(userType);
            }
            if (resumeNavigation === false) {
              this.logger.warn(
                `Redirecting to initial page for EndUser because userType ${userType} is not one of [${allowedEndUserTypes}]. Navigated URL=${route.pathFromRoot
                  .map((s) => s.url.join('/'))
                  .join('/')}`
              );
              // Redirecting silently because it makes no sense to show any message to the user.
              // NOTE: The guard is meant for first navigation to the app for that specific URL.
              // The app itself should never provide a link to navigate to such guarded route. That would be a bug.
              // Hence, a bad link or manipulating the URL in browser will not work.
              this.routerService.navigateToInitialRoute(UserLevel.EndUser, {
                userId: route.params.userId,
              });
            }
            return resumeNavigation;
          }
        })
      );
    }

    return of(resumeNavigation);
  }

  private checkUserLevelAndPermissions(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
    user: AuthUser
  ): boolean {
    let resumeNavigation: boolean = true;

    const allowedUserLevels: UserLevel[] = route.data.allowedUserLevels || [];
    const allowedPermissions: SecurityPermissionName[] = route.data.allowedPermissions || [];
    const requiredUserLevel = route.data.requiredUserLevel;

    if (requiredUserLevel && !userLevelIsEnough(requiredUserLevel, user)) {
      this.logger.warn(
        `User tried to access "${state.url}" but isn't allowed to (required user level: ${route.data.requiredUserLevel}). Redirecting to initial page...`
      );

      this.routerService.navigateToInitialRoute();

      resumeNavigation = false;
    } else {
      const userLevel = this.authenticationService.authUser.role.level;

      if (allowedUserLevels.length > 0 && allowedUserLevels.includes(userLevel) === false) {
        this.logger.warn(
          `User tried to access "${
            route.data.name
          }" but isn't allowed to (allowed user levels are: ${allowedUserLevels.join(
            ', '
          )}). Redirecting to initial page...`
        );

        this.routerService.navigateToInitialRoute();

        resumeNavigation = false;
      }

      if (
        allowedPermissions.length > 0 &&
        this.authenticationService.hasAnyPermission(...allowedPermissions) === false
      ) {
        this.logger.warn(
          `User tried to access "${
            route.data.name
          }" but isn't allowed to (allowed permissions are: ${allowedPermissions.join(
            ', '
          )}). Redirecting to initial page...`
        );

        this.routerService.navigateToInitialRoute();

        resumeNavigation = false;
      }
    }

    return resumeNavigation;
  }

  /**
   * Check if the current enterprise has the given option
   * @param necessaryEnterpriseOption Enterprise option that is necessary for the enterprise to have
   */
  private checkEnterpriseOption(necessaryEnterpriseOption: EnterpriseOptionType): Observable<true | string> {
    return this.enterpriseAuthorizationService.hasEnterpriseOption$(necessaryEnterpriseOption).pipe(
      first(),
      map((hasOption) => hasOption || necessaryEnterpriseOption)
    );
  }

  /**
   * Check if the current enterprise has the given license
   * @param necessaryLicense License that is necessary for the enterprise to have
   */
  private checkEnterpriseLicense(necessaryLicense: License): Observable<true | string> {
    return this.enterpriseAuthorizationService.hasEnterpriseLicense$(necessaryLicense).pipe(
      map((hasLicense) => {
        return hasLicense ? true : necessaryLicense;
      })
    );
  }

  private checkEnterpriseOptionsAndLicense(
    resumeNavigation: boolean,
    route: ActivatedRouteSnapshot
  ): Observable<boolean> {
    let resume$: Observable<boolean>;

    // handling 'hasEnterpriseOption' route activation requirement
    const necessaryEnterpriseOption = route.data.hasEnterpriseOption as EnterpriseOptionType | undefined;
    const necessaryLicense = route.data.hasLicense as License | undefined;

    if (resumeNavigation && (necessaryEnterpriseOption != null || necessaryLicense != null)) {
      const checks$: Observable<true | string>[] = [];

      if (necessaryEnterpriseOption != null) {
        this.logger.debug('Needs enterprise option', necessaryEnterpriseOption, route);

        checks$.push(this.checkEnterpriseOption(necessaryEnterpriseOption));
      }

      if (necessaryLicense != null) {
        this.logger.debug('Needs enterprise license', necessaryLicense, route);

        checks$.push(this.checkEnterpriseLicense(necessaryLicense));
      }

      resume$ = forkJoin(checks$).pipe(
        map((checks) => {
          const missing = checks.filter((check) => check !== true);
          const allowed = missing.length === 0;

          if (allowed === false) {
            const necessary = missing
              .map((value) =>
                this.translateService.instant(
                  `auth-guard.redirect.because.enterprise.is.missing.licenseOrOption.${value}`
                )
              )
              .join(', ');

            const message = this.translateService.instant(
              'auth-guard.redirect.because.enterprise.is.missing.licenseOrOption',
              {
                value: necessary,
              }
            );

            this.routerService.navigateToInitialRoute(UserLevel.EnterpriseAdmin, {
              enterpriseId: route.params.enterpriseId,
            });

            this.notificationService.showInfo(message);
          }

          this.logger.debug('hasEnterpriseOption and/or license: should resume navigation', allowed);

          return allowed;
        })
      );
    } else {
      resume$ = of(resumeNavigation);
    }

    return resume$;
  }

  private checkAuthorization$(resumeNavigation: boolean, route: ActivatedRouteSnapshot): Observable<boolean> {
    let resume$ = of(resumeNavigation);

    if (resumeNavigation && 'authorization' in route.data) {
      resume$ = this.authorizationService.isAuthorized$(route.data.authorization).pipe(first());
    }

    return resume$;
  }

  private checkUserAgainstRoute(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
    authUser: AuthUser
  ): boolean {
    if (route.routeConfig.data?.baseRoute) {
      this.logger.debug(`RouteCheck: Ignoring base route`);

      return true;
    }

    this.logger.debug(`RouteCheck: Checking user level and route params. url=${state.url}`);

    let result: boolean = true;

    if (this.checkEndUser(authUser, route.params) === false) {
      result = false;

      this.routerService.navigateToInitialRoute(UserLevel.EndUser, {
        enterpriseId: authUser.enterpriseName,
        serviceProviderId: authUser.serviceProviderName,
        userId: authUser.username,
      });
    }

    if (this.checkEnterpriseAdmin(authUser, route.params) === false) {
      result = false;

      this.routerService.navigateToInitialRoute(UserLevel.EnterpriseAdmin, {
        enterpriseId: authUser.enterpriseName,
        serviceProviderId: authUser.serviceProviderName,
      });
    }

    if (this.checkServiceProviderAdmin(authUser, route.params) === false) {
      result = false;

      this.routerService.navigateToInitialRoute(UserLevel.ServiceProviderAdmin, {
        serviceProviderId: authUser.serviceProviderName,
      });
    }

    if (result === false) {
      this.logger.warn(
        `RouteCheck: Access not allowed because the user level is insufficient or the route parameters do not match, redirecting user to initial route.`
      );
    }

    return result;
  }

  private checkEndUser(user: AuthUser, routeParams: Record<string, string>): boolean {
    if (user.role.level !== UserLevel.EndUser) {
      return true;
    }

    const { enterpriseId, serviceProviderId, userId } = routeParams;
    const userMatches = userId === user.username;
    const enterpriseMatches = enterpriseId === user.enterpriseName;
    const resellerMatches = serviceProviderId === user.serviceProviderName;

    return userMatches && enterpriseMatches && resellerMatches;
  }

  private checkEnterpriseAdmin(user: AuthUser, routeParams: Record<string, string>): boolean {
    if (user.role.level !== UserLevel.EnterpriseAdmin) {
      return true;
    }

    const { enterpriseId, serviceProviderId } = routeParams;
    const enterpriseMatches = enterpriseId === user.enterpriseName;
    const resellerMatches = serviceProviderId === user.serviceProviderName;

    return enterpriseMatches && resellerMatches;
  }

  private checkServiceProviderAdmin(user: AuthUser, routeParams: Record<string, string>): boolean {
    if (user.role.level !== UserLevel.ServiceProviderAdmin) {
      return true;
    }

    const { serviceProviderId } = routeParams;
    const resellerMatches = serviceProviderId === user.serviceProviderName;
    const systemAdminMatches =
      serviceProviderId == null && user.role.permissions.includes(SecurityPermissionName.MANAGE_SYSTEM);

    return resellerMatches || systemAdminMatches;
  }
}
